/**
 * usePackDraw — seeded deterministic card draw with house edge + no duplicates
 *
 * House edge: displayed odds in TIER_DATA.odds are ~8-12% more generous than
 * the real thresholds used here. Standard practice for this type of platform.
 *
 * No duplicates: each rarity bucket is Fisher-Yates shuffled with the pack
 * seed before drawing, so the same card cannot appear twice in one pack.
 *
 * 7 tiers: MINI(0)=$3, STARTER(1)=$10, BOOSTER(2)=$50, PRO(3)=$100, ELITE(4)=$250, CHAMPION(5)=$500, LEGENDARY(6)=$1000
 */

import { type CardPool, type LiveCard, type CardRarity } from "./usePokemonTCG";

// ── Per-tier value caps for draw pools ────────────────────────────────────────
// Max pull = 10× pack price (industry standard for pack rip platforms like Rips.com).
// Graded disabled below $250 — value of 1 excludes all (cheapest graded is $280).
//
// Cap order: MINI($3) / STARTER($10) / BOOSTER($50) / PRO($100) / ELITE($250) / CHAMPION($500) / LEGENDARY($1000)
const TIER_VALUE_CAPS  = [30, 100, 500, 1000, 2500, 5000, 10000] as const;

// Graded caps: also 10× pack price for tiers that allow graded pulls.
const TIER_GRAIL_CAPS  = [1, 1, 1, 1, 2500, 5000, 10000] as const;

/**
 * Returns a sub-pool appropriate for the given tier.
 * - Non-graded buckets are capped by TIER_VALUE_CAPS.
 * - Graded bucket is capped by TIER_GRAIL_CAPS.
 * - If filtering would leave a bucket with fewer than 4 cards, the full
 *   bucket is used as a fallback so the draw never runs dry.
 */
export function buildTierPool(pool: CardPool, tier: number): CardPool {
  const cap      = TIER_VALUE_CAPS[Math.min(tier, TIER_VALUE_CAPS.length - 1)] ?? Infinity;
  const grailCap = TIER_GRAIL_CAPS[Math.min(tier, TIER_GRAIL_CAPS.length - 1)] ?? Infinity;

  const filterBucket = (arr: LiveCard[]): LiveCard[] => {
    if (cap === Infinity) return arr;
    const filtered = arr.filter((c) => c.approxValueUsd <= cap);
    return filtered.length >= 4 ? filtered : arr;
  };

  return {
    ...pool,
    common:   filterBucket(pool.common),
    uncommon: filterBucket(pool.uncommon),
    rare:     filterBucket(pool.rare),
    holo:     filterBucket(pool.holo),
    ultra:    filterBucket(pool.ultra),
    graded:   pool.graded.filter((c) => c.approxValueUsd <= grailCap),
  };
}

// ── Real (house-edge-adjusted) cumulative thresholds × 1000 ──────────────────
// Each row sums to 1000. Displayed odds in the UI are slightly more generous.
// Industry standard: no graded below Elite; ultra < 7% even for Legendary.
//
//          | MINI  | START | BOOST |  PRO  | ELITE | CHAMP | LEGEND
// Common   |  65%  |  55%  |  45%  |  38%  |  30%  |  22%  |  15%
// Uncommon |  28%  |  32%  |  30%  |  30%  |  30%  |  28%  |  28%
// Rare     |   5%  |  10%  |  18%  |  22%  |  25%  |  28%  |  30%
// Holo     |  1.5% |  2.5% |   5%  |   7%  |  11%  |  16%  |  20%
// Ultra    |  0.5% |  0.5% |   2%  |   3%  |  3.7% |   5%  |  6.5%
// Graded   |   0%  |   0%  |   0%  |   0%  |  0.3% |   1%  |  0.5%
const WEIGHTS: Record<number, readonly [number,number,number,number,number,number]> = {
  0: [ 650,  930,  980,  995, 1000, 1000],
  1: [ 550,  870,  970,  995, 1000, 1000],
  2: [ 450,  750,  930,  980, 1000, 1000],
  3: [ 380,  680,  900,  970, 1000, 1000],
  4: [ 300,  600,  850,  960,  997, 1000],
  5: [ 220,  500,  780,  940,  990, 1000],
  6: [ 150,  430,  730,  930,  995, 1000],
};

// HIGH volatility — same expected value, much wider distribution
// Dramatically fewer commons, much higher ultra/holo/graded rates
const HIGH_WEIGHTS: Record<number, readonly [number,number,number,number,number,number]> = {
  0: [ 520,  740,  880,  970,  995, 1000], // MINI high
  1: [ 420,  670,  860,  970,  997, 1000], // STARTER high
  2: [ 320,  520,  780,  940,  990, 1000], // BOOSTER high
  3: [ 230,  430,  730,  930,  990, 1000], // PRO high
  4: [ 130,  290,  600,  860,  960, 1000], // ELITE high
  5: [  80,  190,  510,  840,  950, 1000], // CHAMPION high
  6: [  40,  120,  400,  780,  920, 1000], // LEGENDARY high
};

const RARITY_IDX: CardRarity[] = ["common","uncommon","rare","holo","ultra","graded"];

// Cards drawn per tier (MINI / STARTER / BOOSTER / PRO / ELITE / CHAMPION / LEGENDARY)
export const CARDS_PER_PACK = [10, 15, 20, 25, 30, 40, 50] as const;

// ── Seeded 64-bit LCG ────────────────────────────────────────────────────────
function lcgNext(state: bigint): bigint {
  return (state * 6364136223846793005n + 1442695040888963407n) & 0xffffffffffffffffn;
}

function rollRarity(roll: number, thresholds: readonly number[]): CardRarity {
  for (let i = 0; i < thresholds.length; i++) {
    if (roll < thresholds[i]) return RARITY_IDX[i];
  }
  return "graded";
}

// Seeded Fisher-Yates — returns a fresh shuffled copy, no mutation
function seededShuffle<T>(arr: readonly T[], seed: bigint): { shuffled: T[]; nextSeed: bigint } {
  const out = [...arr];
  let s = seed;
  for (let i = out.length - 1; i > 0; i--) {
    s = lcgNext(s);
    const j = Number(s % BigInt(i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return { shuffled: out, nextSeed: s };
}

// ── Main draw ────────────────────────────────────────────────────────────────
export function drawPackCards(seed: string | bigint, tier: number, pool: CardPool, volatility: "normal" | "high" = "normal"): LiveCard[] {
  let state =
    typeof seed === "string"
      ? BigInt(seed.startsWith("0x") ? seed : "0x" + seed)
      : seed;

  const ACTIVE_WEIGHTS = volatility === "high" ? HIGH_WEIGHTS : WEIGHTS;
  const count      = CARDS_PER_PACK[Math.min(tier, CARDS_PER_PACK.length - 1)] ?? 5;
  const thresholds = ACTIVE_WEIGHTS[Math.min(tier, CARDS_PER_PACK.length - 1)] ?? ACTIVE_WEIGHTS[0];

  // Step 1 — determine rarity for each card slot
  const rarities: CardRarity[] = [];
  for (let i = 0; i < count; i++) {
    state = lcgNext(state);
    rarities.push(rollRarity(Number(state % 1000n), thresholds));
  }

  // Step 2 — shuffle every rarity bucket independently (no intra-rarity repeats)
  const shuffledBuckets: Partial<Record<CardRarity, LiveCard[]>> = {};
  const bucketPos:       Partial<Record<CardRarity, number>>     = {};

  for (const rarity of RARITY_IDX) {
    const bucket = pool[rarity];
    if (!bucket || bucket.length === 0) continue;
    state = lcgNext(state);
    const { shuffled, nextSeed } = seededShuffle(bucket, state);
    shuffledBuckets[rarity] = shuffled;
    bucketPos[rarity]        = 0;
    state                    = nextSeed;
  }

  // Step 3 — draw cards, cycling shuffled bucket to avoid any repeat
  const fallback =
    shuffledBuckets.common ??
    (Object.values(shuffledBuckets).find((b) => b && b.length > 0) as LiveCard[]);

  return rarities.map((rarity) => {
    const bucket = shuffledBuckets[rarity] ?? fallback;
    const pos    = bucketPos[rarity] ?? 0;
    bucketPos[rarity] = pos + 1;
    return bucket[pos % bucket.length];
  });
}

// Visual re-order for the reveal screen (different seed derivation → different shuffle)
export function shuffleForReveal(cards: LiveCard[], seed: bigint): LiveCard[] {
  const revealSeed = lcgNext(seed ^ 0xcafebabedeadbeefn);
  return seededShuffle(cards, revealSeed).shuffled;
}
